/*
 * Copyright (c) 2023 Roboto Technologies, Inc.
 */
import { Box, useTheme } from "@mui/material";
import classNames from "classnames";
import { PluggableList } from "node_modules/react-markdown/lib";
import * as React from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import rehypeExternalLinks from "rehype-external-links";
import remarkEmoji from "remark-emoji";
import remarkGfm from "remark-gfm";

import {
  getDatasetLinkContext,
  getFileLinkContext,
  getMessagePathLinkContext,
  getTopicLinkContext,
  isDatasetLink,
  isFileLink,
  isMessagePathLink,
  isTimestampLink,
  isTopicLink,
} from "@/shared/components/RobotoMarkdown/magicLinks.ts";
import {
  browserSupportsNamedGroups,
  deserializeTimestamp,
} from "@/shared/text";

import {
  preProcessContent,
  convertToMarkdownWriteToClipboard,
} from "./helpers";
import styles from "./RobotoMarkdown.module.css";
import { Timestamp } from "./Timestamp";

export type DatasetLinkRenderer = (props: {
  datasetId: string;
  text: string;
  time?: bigint;
}) => React.ReactElement;

export type FileLinkRenderer = (props: {
  fileId: string;
  text: string;
  time?: bigint;
}) => React.ReactElement;

export type TopicLinkRenderer = (props: {
  topicId: string;
  text: string;
  time?: bigint;
}) => React.ReactElement;

export type MessagePathLinkRenderer = (props: {
  messagePathId: string;
  text: string;
  time?: bigint;
}) => React.ReactElement;

type RobotoMarkdownProps = {
  content: string;
  className?: string;
  datasetLinkRenderer?: DatasetLinkRenderer;
  fileLinkRenderer?: FileLinkRenderer;
  topicLinkRenderer?: TopicLinkRenderer;
  messagePathLinkRenderer?: MessagePathLinkRenderer;
};

export const RobotoMarkdown: React.FC<RobotoMarkdownProps> = (props) => {
  const theme = useTheme();

  // Conditionally create remarkPlugins based on named group support
  const remarkPlugins: PluggableList = [
    remarkEmoji,
    ...(browserSupportsNamedGroups() ? [remarkGfm] : []),
  ];

  // Pre-process the mentions, place the @ symbol inside the link for correct formatting
  // and make the link navigate to '/users/{userId}'
  const formattedContent = preProcessContent(props.content);

  return (
    <Box
      data-cy="markdown-container"
      onCopy={(e) => {
        e.preventDefault();

        const selection = window.getSelection();
        if (!selection) {
          return;
        }

        // generate markdown from single element
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          let commonAncestor: Node | HTMLElement | null =
            range.commonAncestorContainer;

          if (commonAncestor.nodeType === Node.TEXT_NODE) {
            commonAncestor = commonAncestor.parentElement;
          }

          const selectedContent = range.cloneContents();

          if (
            commonAncestor &&
            commonAncestor?.textContent?.trim() ===
              selection.toString().trim() &&
            selectedContent.childNodes.length === 1
          ) {
            const anchorNode = selection.anchorNode;
            const parentElement = anchorNode?.parentElement;
            const htmlString = parentElement?.outerHTML ?? "";

            convertToMarkdownWriteToClipboard(htmlString);

            return;
          }
        }

        // generate markdown from multiple elements
        const container = document.createElement("div");
        for (let i = 0; i < selection.rangeCount; i++) {
          container.appendChild(selection.getRangeAt(i).cloneContents());
        }

        convertToMarkdownWriteToClipboard(container.innerHTML);
      }}
    >
      <ReactMarkdown
        rehypePlugins={[[rehypeExternalLinks, { target: "_blank" }]]}
        remarkPlugins={remarkPlugins}
        className={classNames(styles.markdownContainer, props.className)}
        urlTransform={(url) => {
          if (
            isTimestampLink(url) ||
            isDatasetLink(url) ||
            isFileLink(url) ||
            isTopicLink(url) ||
            isMessagePathLink(url)
          ) {
            return url;
          }

          return defaultUrlTransform(url);
        }}
        components={{
          a: ({ href, children }) => {
            if (href) {
              try {
                if (isTimestampLink(href)) {
                  const timestamp = deserializeTimestamp(href);
                  return (
                    <Timestamp timestamp={timestamp}>{children}</Timestamp>
                  );
                }

                if (props.datasetLinkRenderer) {
                  const datasetLinkContext = getDatasetLinkContext(href);
                  if (datasetLinkContext) {
                    return props.datasetLinkRenderer({
                      datasetId: datasetLinkContext.dataset.dataset_id,
                      text: String(children),
                      time: datasetLinkContext.dataset.t,
                    });
                  }
                }

                if (props.fileLinkRenderer) {
                  const fileLinkContext = getFileLinkContext(href);
                  if (fileLinkContext) {
                    return props.fileLinkRenderer({
                      fileId: fileLinkContext.file.file_id,
                      text: String(children),
                      time: fileLinkContext.file.t,
                    });
                  }
                }

                if (props.topicLinkRenderer) {
                  const topicLinkContext = getTopicLinkContext(href);
                  if (topicLinkContext) {
                    return props.topicLinkRenderer({
                      topicId: topicLinkContext.topic.topic_id,
                      text: String(children),
                      time: topicLinkContext.topic.t,
                    });
                  }
                }

                if (props.messagePathLinkRenderer) {
                  const messagePathLinkContext =
                    getMessagePathLinkContext(href);
                  if (messagePathLinkContext) {
                    return props.messagePathLinkRenderer({
                      messagePathId:
                        messagePathLinkContext.message_path.message_path_id,
                      text: String(children),
                      time: messagePathLinkContext.message_path.t,
                    });
                  }
                }
              } catch {
                // Do nothing, we'll fall back to default naive rendering behavior
              }
            }

            return (
              <a
                href={href}
                style={{
                  color: theme.palette.primary.main,
                  fontWeight: 500,
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {children}
              </a>
            );
          },
          pre: ({ children }) => (
            <pre
              style={{
                overflow: "auto",
                backgroundColor: theme.palette.code.main,
                padding: theme.spacing(1),
                marginBottom: theme.spacing(1.5),
                whiteSpace: "pre-wrap",
              }}
            >
              {children}
            </pre>
          ),
          ul: ({ children }) => (
            <ul
              style={{
                paddingLeft: theme.spacing(2),
                whiteSpace: "normal",
              }}
            >
              {children}
            </ul>
          ),
          li: ({ children }) => (
            <li
              style={{
                padding: theme.spacing(1),
                whiteSpace: "normal",
              }}
            >
              {children}
            </li>
          ),
          ol: ({ children }) => (
            <ol
              style={{
                padding: theme.spacing(1),
                paddingLeft: theme.spacing(2),
                whiteSpace: "normal",
              }}
            >
              {children}
            </ol>
          ),
          h1: ({ children }) => (
            <h1
              style={{
                fontSize: "0.98rem",
                fontWeight: 500,
              }}
            >
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2
              style={{
                fontSize: "0.95rem",
                fontWeight: 500,
              }}
            >
              {children}
            </h2>
          ),
        }}
      >
        {formattedContent}
      </ReactMarkdown>
    </Box>
  );
};
